<template>
  <div class="vms-licenses-page">
    <VmsPageHeader
      kicker="Licensing"
      title="Licenses"
      icon="mdi-license"
    >
      <template #actions>
        <VmsColumnVisibility
          v-model="selectedHeaders"
          :headers="headers"
          @change="toggleColumnVisibility"
        />
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              dark
              class="text-capitalize vms-primary-cta"
              elevation="0"
              @click="newLicenseModal"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon left>mdi-license</v-icon>
              <span>New License</span>
            </v-btn>
          </template>
          <span>Enter New License</span>
        </v-tooltip>
      </template>
    </VmsPageHeader>

    <VmsResponsiveList
      :items="watchmenWithIndex"
      :loading="loading"
      loading-text="Loading licenses…"
    >
      <template #table>
        <v-data-table
          :headers="computedHeaders"
          :items="watchmenWithIndex"
          sort-by="name"
          class="elevation-0 vms-surface"
          :loading="loading"
          loading-text="Loading... Please wait"
        >
          <template v-slot:item.approved="{ item }">
            <v-chip :color="getStatusColor(item.approved)" dark>
              {{ getStatusText(item.approved) }}
            </v-chip>
          </template>

          <template v-slot:no-data>
            <a class="info--text text--darken-1">
              No active licenses are available. Contact support to request for a new License.
            </a>
          </template>
        </v-data-table>
      </template>

      <template #card="{ item }">
        <div class="vms-list-card__title">{{ item.license_key }}</div>
        <div class="vms-list-card__meta">
          <span>{{ item.period_title }}</span>
          <v-chip small :color="getStatusColor(item.approved)" dark>
            {{ getStatusText(item.approved) }}
          </v-chip>
        </div>
        <div class="vms-list-card__row">
          <span>{{ item.valid_from }} – {{ item.valid_to }}</span>
        </div>
      </template>

      <template #empty>
        <div class="vms-empty">
          <a class="info--text text--darken-1">
            No active licenses are available. Contact support to request for a new License.
          </a>
        </div>
      </template>
    </VmsResponsiveList>

    <v-dialog v-model="showLicenseModal" max-width="600">
      <v-card>
        <v-card-title class="grey--text text-h5">
          Enter License Key
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="mb-0 pb-0">
          <v-form class="pt-7 pb-0" ref="form">
            <v-row>
              <v-col cols="12" md="12" sm="12">
                <v-autocomplete 
                  dense 
                  v-model="license.period_id" 
                  :items="periods" 
                  item-value="id"
                  item-text="title" 
                  label="Billing Cycle/Period" 
                  placeholder="Choose a Billing Cycle/Period"
                  :rules="rules.select"
                  outlined
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="12" sm="12">
                <v-otp-input
                  dense
                  v-model="license.license_key"
                  label="License Key"
                  length="16"
                  :rules="rules.required"
                ></v-otp-input>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>

          <v-btn color="red darken-1" text @click="closeModal" class="text-capitalize mx-1">
            Cancel
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn color="green darken-1 white--text" @click="validateLicenseKey" class="text-capitalize mx-1">
            Validate
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EntriesService from "../../services/entries.service";
import LicensesService from "../../services/licenses.service";
import License from "../../models/license";
import VmsPageHeader from "../../components/VmsPageHeader.vue";
import VmsResponsiveList from "../../components/VmsResponsiveList.vue";
import VmsColumnVisibility from "../../components/VmsColumnVisibility.vue";
import columnVisibility from "../../mixins/columnVisibility";

export default {
  components: {
    VmsPageHeader,
    VmsResponsiveList,
    VmsColumnVisibility,
  },
  mixins: [columnVisibility],

  data: () => ({
    name: "Licenses",
    loading: true,
    items: [],
    editedIndex: -1,
    editedItem: {
      name: "",
      attachment: "",
      attachment_file: [],
    },
    defaultItem: {
      name: "",
      attachment: "",
      attachment_file: [],
    },
    remember: 0,
    timeout: 2000,
    dialog: false,
    valid: true,
    rules: {
      select: [(v) => !!v || "An item should be selected"],
      select2: [(v) => v.length > 0 || "Atleast one item should be selected"],
      required: [
        (v) => !!v || "Field is required",
        (v) => (v && v.length >= 16) || "Min 16 characters",
      ],
      file: [
        (v) => !!v || "File is required",
        (v) => (v && v.length > 0) || "File is required",
      ],
    },
    search: '',
    headers: [
      {
        text: "#",
        align: "start",
        filterable: true,
        value: "index",
        visible: true,
      },
      {
        text: "License Key",
        value: "license_key",
        visible: true,
      },
      {
        text: "Period",
        value: "period_title",
        visible: true,
      },
      {
        text: "Valid From",
        value: "valid_from",
        visible: true,
      },
      {
        text: "Valid To",
        value: "valid_to",
        visible: true,
      },
      {
        text: "Approver",
        value: "approver_name",
        visible: false,
      },
      {
        text: "Status",
        value: "approved",
        visible: true,
      },
    ],
    license: new License(),
    passage_type: null,
    showLicenseModal: false,
    apiErrors: [],
    editWatchman: false,
    activeLicenses: [],
    periods: [],
    facility_entry_points: [],
    facilityID: '',
    closeOnContentClick: true,
    fromDate: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
    toDate: '',
    entryID: '',
    // Info modal functionality variables
    showInfo: false,
    currentStep: 1
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New" : "Edit";
    },
    loggedIn() {
      return this.$store.state.auth.status.loggedIn;
    },
    getLoading() {
      return this.loading;
    },
    org() {
      return JSON.parse(localStorage.getItem("org"));
    },
    watchmenWithIndex() {
      return this.activeLicenses.map(
        (items, index) => ({
          ...items,
          index: index + 1,
        })
      )
    },

  },

  watch: {
    entryID(newEntryID, oldEntryID) {
      if (newEntryID !== oldEntryID) {
        this.extractPassageType();
      }
    },

    facilityID(newFacilityID, oldFacilityID) {
      if (newFacilityID !== oldFacilityID) {
        this.getEntryPoints();
      }
    }
  },

  created() {
    this.initColumnVisibility();
    this.$nextTick(() => {
      this.getLicenses();
      this.getPeriods();
    });
  },

  methods: {
    openDocument(item) {
      console.log(item);
      this.$router.replace(item.attachment);
    },

    handleLogout() {
      this.$store.dispatch("auth/logout");
      this.$router.push("/login");
    },

    validate() {
      this.$refs.form.validate();
    },

    reset() {
      this.$refs.form.reset();
    },

    resetValidation() {
      this.$refs.form.resetValidation();
    },

    getLicenses() {
      LicensesService.index().then(
        (response) => {
          if (response.status === 200) {
            this.activeLicenses = response.data.data;
            this.loading = false;
          } else {
            this.activeLicenses = [];
            console.log(response);
          }
        },
        (error) => {
          this.activeLicenses = [];
          console.log(error);
          if (error.response.status === 401) {
            this.handleLogout();
          }
        }
      );
    },

    getPeriods() {
      LicensesService.periods().then(
        (response) => {
          if (response.status === 200) {
            this.periods = response.data.data;
          }
        },
        (error) => {
          console.log(error);
        }
      );
    },

    newLicenseModal() {
      this.getLicenses();
      this.getPeriods();
      this.showLicenseModal = true;
    },

    closeModal() {
      this.showLicenseModal = false;
      this.editWatchman = false;
      this.facility_entry_points = [];
      this.fromDate = (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10);
      this.toDate = '';
      this.passage_type = null;
      this.license = new License();
    },

    validateLicenseKey() {
      let valid = this.$refs.form.validate();
      if (valid) {
        LicensesService.store(this.license).then(
          (response) => {
            if (response.status === 200) {
              this.activeLicenses.push(this.editedItem);
              this.$store.dispatch(
                "alert/success",
                "License validated successfully"
              );
              this.loading = false;
              this.getLicenses();
              this.closeModal();
            } else {
              this.$store.dispatch("alert/error", "Validates license key successfully");
              this.getLicenses();
            }
          },
          (error) => {
            if (error.response.status === 422) {
              this.$store.dispatch(
                "alert/error",
                error.response.data.message
              );
  
              this.apiErrors = error.response.data.errors
              this.loading = false;
              this.getLicenses();
            }

            if (error.response.status === 401) {
              this.handleLogout();
              this.loading = false;
              this.getLicenses();
            }
  
            if (error.response.status === 404) {
              this.$store.dispatch("alert/error", error.response.data.message);
              this.closeModal();
            }

            if (error.response.status === 500) {
              this.$store.dispatch("alert/error", error.response.data.message);
              this.getLicenses();
              this.closeModal();
            }
          }
        );
      }
    },

    getEntryPoints() {
      EntriesService.facilityEntryPoints(this.license.fcl_id).then(
        entries => {
          this.facility_entry_points = entries.data.data
          console.log(this.facility_entry_points)
        },
        fail => {
          console.log(fail)
        }
      );
    },

    extractPassageType() {
      let points = this.facility_entry_points;
      if ((this.facility_entry_points).length !== 0) {
        points.forEach(entry => {
          if (entry.id === this.entryID) {
            this.passage_type = entry.relationships.type.attributes.type
          }
        });
      }
    },

    getStatusColor(status) {
      if (status == 2) return 'red';
      else if (status == 0) return 'info';
      else return 'green';
    },
    
    getStatusText(status) {
      if (status == 2) return 'Rejected';
      else if (status == 0) return 'Pending';
      else return 'Approved';
    },
  },

  mounted() {
    // Set the nearest positioned ancestor to the body element
    document.body.style.position = 'relative';
  }
};
</script>
